import type { TOC } from '@ember/component/template-only';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import { PhUser, PhStar, PhCalendarCheck, PhChatCircle, PhCertificate } from 'ember-phosphor-icons';

/**
 * Coach profile information
 */
export interface Coach {
  id: string;
  name: string;
  title: string;
  photo?: string;
  specialties: string[];
  rating: number;
  reviewCount: number;
  yearsExperience: number;
  credentials: string[];
  availableSlots: number;
  bio: string;
}

/**
 * CoachCard - Displays a coach profile card
 *
 * Shows coach information including photo, name, specialties,
 * credentials, ratings, and availability with booking CTA.
 *
 * @example
 * ```gts
 * <CoachCard
 *   @coach={{this.coach}}
 *   @onSchedule={{this.openScheduler}}
 * />
 * ```
 */
interface CoachCardSignature {
  Args: {
    /** Coach profile data */
    coach: Coach;
    /** Handler for schedule button click */
    onSchedule?: (coach: Coach) => void;
    /** Whether to show compact view */
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

const renderStars = (rating: number) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const stars = [];

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push('full');
    } else if (i === fullStars && hasHalfStar) {
      stars.push('half');
    } else {
      stars.push('empty');
    }
  }
  return stars;
};

const CoachCard: TOC<CoachCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <div class="tio-coach-card" ...attributes>
    {{! Header with gradient background }}
    <div class="tio-coach-card__header">
      <div class="tio-coach-card__header-overlay"></div>
      <div class="relative tio-row-loose">
        {{! Photo or placeholder }}
        <div class="tio-coach-card__avatar">
          {{#if @coach.photo}}
            <img
              src={{@coach.photo}}
              alt="{{@coach.name}}"
              class="tio-coach-card__avatar-image"
              loading="lazy"
            />
          {{else}}
            <PhUser @size={{40}} class="tio-coach-card__avatar-placeholder" />
          {{/if}}
        </div>

        <div class="flex-1">
          <Heading @level={{3}} class="tio-fg-inverse mb-1">
            {{@coach.name}}
          </Heading>
          <Text @variant="inherit" @size="sm" class="tio-coach-card__title">
            {{@coach.title}}
          </Text>

          {{! Rating }}
          <div class="tio-row-tight mt-2">
            <div class="tio-row-flush gap-0.5">
              {{#each (renderStars @coach.rating) as |star|}}
                {{#if (eq star "full")}}
                  <PhStar @size={{14}} @weight="fill" class="tio-coach-card__star--filled" />
                {{else if (eq star "half")}}
                  <PhStar
                    @size={{14}}
                    @weight="fill"
                    class="tio-coach-card__star--filled tio-coach-card__star--half"
                  />
                {{else}}
                  <PhStar @size={{14}} class="tio-coach-card__star--empty" />
                {{/if}}
              {{/each}}
            </div>
            <Text @variant="inherit" @size="xs" class="tio-coach-card__rating">
              {{@coach.rating}}
              ({{@coach.reviewCount}}
              reviews)
            </Text>
          </div>
        </div>
      </div>
    </div>

    {{! Body content }}
    <div class="p-6 space-y-4">
      {{! Experience & Credentials }}
      <div class="tio-row-loose text-sm tio-fg-secondary">
        <div class="tio-row-extra-tight">
          <PhCertificate @size={{16}} class="tio-fg-brand" />
          <span>{{@coach.yearsExperience}}+ years</span>
        </div>
        <div class="tio-row-extra-tight">
          <PhCalendarCheck @size={{16}} class="tio-fg-success" />
          <span>{{@coach.availableSlots}} slots this week</span>
        </div>
      </div>

      {{! Specialties }}
      <div class="tio-wrap-tight">
        {{#each @coach.specialties as |specialty|}}
          <Chip class="text-xs tio-bg-brand-soft tio-fg-brand">
            {{specialty}}
          </Chip>
        {{/each}}
      </div>

      {{! Bio preview }}
      {{#unless @compact}}
        <Text @size="sm" class="tio-fg-secondary line-clamp-2">
          {{@coach.bio}}
        </Text>
      {{/unless}}

      {{! Credentials }}
      <div class="pt-2 border-t tio-border-subtle">
        <Text @size="xs" class="tio-fg-muted mb-2">
          Credentials
        </Text>
        <div class="tio-wrap-extra-tight">
          {{#each @coach.credentials as |credential|}}
            <span class="text-xs px-2 py-0.5 tio-bg-sunken tio-fg-secondary rounded">
              {{credential}}
            </span>
          {{/each}}
        </div>
      </div>

      {{! Actions }}
      <div class="tio-flex pt-2">
        {{#if @onSchedule}}
          <Button
            @intent="primary"
            @appearance="outlined"
            class="flex-1"
            {{on "click" (fn @onSchedule @coach)}}
          >
            <PhCalendarCheck @size={{16}} class="mr-2" />
            Schedule
          </Button>
        {{else}}
          <Button @intent="primary" @appearance="outlined" class="flex-1">
            <PhCalendarCheck @size={{16}} class="mr-2" />
            Schedule
          </Button>
        {{/if}}
        <Button @intent="primary" @appearance="minimal" class="flex-1">
          <PhChatCircle @size={{16}} class="mr-2" />
          Message
        </Button>
      </div>
    </div>
  </div>
</template>;

export default CoachCard;
